import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import type { BleDeviceInfoExtended, BleServiceResponse, DeviceData } from '../../shared/interfaces/ble.interface';
import { BleConnectionState } from '../../shared/interfaces/ble.interface';

import { MOCK_HUNTING_DEVICE_INFO, MOCK_HUNTING_DEVICE_NAME, MOCK_HUNTING_DEVICE_SERIAL } from './mock-hunting-device.constants';
import { MockHuntingDeviceGate } from './mock-hunting-device-gate.service';

const MOCK_INITIAL_DEVICE_DATA: DeviceData = {
  batteryLevel: 87,
  brightnessLevel: 5,
  serialNumber: MOCK_HUNTING_DEVICE_SERIAL,
  buildVersion: '1.0.0-mock',
  bleLocalName: MOCK_HUNTING_DEVICE_NAME,
  batteryLevels: [87, 0, 0],
  batteryTemperatures: [220, -32768, -32768],
  wifiEnabled: true,
  contrastLevel: 5,
  imageMode: 0,
};

/**
 * Serves canned reads/writes for the fake Cannes device so the settings + sync screens run with no
 * hardware. Inert until MockHuntingDeviceGate flips active — BleDeviceManagerFacade only routes
 * here while it is.
 */
@Injectable()
export class MockBleDeviceManagerService {
  // services
  private readonly gate = inject(MockHuntingDeviceGate);

  // state
  private readonly connectionStateSubject$ = new BehaviorSubject<BleConnectionState>(BleConnectionState.DISCONNECTED);
  private readonly connectedDeviceSubject$ = new BehaviorSubject<BleDeviceInfoExtended | undefined>(undefined);
  private readonly deviceDataSubject$ = new BehaviorSubject<DeviceData>({});
  private readonly scannedDevicesSubject$ = new BehaviorSubject<Array<BleDeviceInfoExtended>>([]);
  private readonly bleEnabledSubject$ = new BehaviorSubject<boolean>(true);

  // public observables
  public readonly connectionState$ = this.connectionStateSubject$.asObservable();
  public readonly connectedDevice$ = this.connectedDeviceSubject$.asObservable();
  public readonly deviceData$ = this.deviceDataSubject$.asObservable();
  public readonly scannedDevices$ = this.scannedDevicesSubject$.asObservable();
  public readonly bleEnabled$ = this.bleEnabledSubject$.asObservable();
  // The inert mock never has an unexpected disconnect, so this is an empty stream.
  public readonly onUnexpectedDisconnect$ = new Observable<{ deviceId: string; timestamp: number }>();

  constructor() {
    this.gate.active$.subscribe(active => {
      if (active) {
        this.connectionStateSubject$.next(BleConnectionState.CONNECTED);
        this.connectedDeviceSubject$.next(MOCK_HUNTING_DEVICE_INFO);
        this.deviceDataSubject$.next({ ...MOCK_INITIAL_DEVICE_DATA });
      } else {
        this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
        this.connectedDeviceSubject$.next(undefined);
        this.deviceDataSubject$.next({});
      }
    });
  }

  public readBatteryLevel$(): Observable<BleServiceResponse<number>> {
    return this.ok$(this.deviceDataSubject$.value.batteryLevel ?? 0);
  }

  public readBrightnessLevel$(): Observable<BleServiceResponse<number>> {
    return this.ok$(this.deviceDataSubject$.value.brightnessLevel ?? 0);
  }

  public writeBrightnessLevel$(level: number): Observable<BleServiceResponse<void>> {
    this.patchDeviceData({ brightnessLevel: level });
    return this.ok$(undefined);
  }

  public writeContrastLevel$(level: number): Observable<BleServiceResponse<void>> {
    this.patchDeviceData({ contrastLevel: level });
    return this.ok$(undefined);
  }

  public writeImageMode$(mode: number): Observable<BleServiceResponse<void>> {
    this.patchDeviceData({ imageMode: mode });
    return this.ok$(undefined);
  }

  public writeConnectivityEnabled$(_enabled: boolean): Observable<BleServiceResponse<void>> {
    return this.ok$(undefined);
  }

  public writeWifiEnabled$(enabled: boolean): Observable<BleServiceResponse<void>> {
    this.patchDeviceData({ wifiEnabled: enabled });
    return this.ok$(undefined);
  }

  public readAccessPointSSID$(): Observable<BleServiceResponse<string>> {
    return this.ok$('MOCK-CANNES-AP');
  }

  public startStreaming$(): Observable<BleServiceResponse<void>> {
    return this.ok$(undefined);
  }

  public stopStreaming$(): Observable<BleServiceResponse<void>> {
    return this.ok$(undefined);
  }

  public readStreamingStatus$(): Observable<BleServiceResponse<boolean>> {
    return this.ok$(true);
  }

  public writeRecordVideo$(_recording: boolean): Observable<BleServiceResponse<void>> {
    return this.ok$(undefined);
  }

  public takePicture$(): Observable<BleServiceResponse<void>> {
    return this.ok$(undefined);
  }

  public writeAccessPointPassphrase$(_passphrase: string): Observable<BleServiceResponse<void>> {
    return this.ok$(undefined);
  }

  public writeWifiMode$(_mode: number): Observable<BleServiceResponse<void>> {
    return this.ok$(undefined);
  }

  public loadSettingsData$(): Observable<{ brightnessLevel: number; wifiEnabled: boolean; contrastLevel: number; imageMode: number }> {
    const data = this.deviceDataSubject$.value;
    return of({
      brightnessLevel: data.brightnessLevel ?? 0,
      wifiEnabled: data.wifiEnabled ?? false,
      contrastLevel: data.contrastLevel ?? 0,
      imageMode: data.imageMode ?? 0,
    });
  }

  public readDeviceInfo$(): Observable<BleServiceResponse<Partial<DeviceData>>> {
    return this.ok$({ ...this.deviceDataSubject$.value });
  }

  public loadInitialData$(): Observable<void> {
    // Re-emit so subscribers that only react to loadInitialData$ still see the seeded state.
    this.deviceDataSubject$.next({ ...this.deviceDataSubject$.value });
    return of(undefined);
  }

  public readBleLocalName$(): Observable<BleServiceResponse<string>> {
    return this.ok$(this.deviceDataSubject$.value.bleLocalName ?? MOCK_HUNTING_DEVICE_NAME);
  }

  public writeBleLocalName$(name: string): Observable<BleServiceResponse<void>> {
    this.patchDeviceData({ bleLocalName: name });
    return this.ok$(undefined);
  }

  public disconnect$(): Observable<BleServiceResponse<void>> {
    this.connectionStateSubject$.next(BleConnectionState.DISCONNECTED);
    this.connectedDeviceSubject$.next(undefined);
    return this.ok$(undefined);
  }

  public scanForDevices$(): Observable<void> {
    return of(undefined);
  }

  // Auto-reconnect resolves to the fake Cannes device immediately (no scan needed) so the retry
  // loop can run against the dev toggle. The real connectToDevice$ still fails as designed.
  public waitForDevice$(deviceId: string, _timeoutMs = 15_000): Observable<BleDeviceInfoExtended> {
    return of(MOCK_HUNTING_DEVICE_INFO);
  }

  // Fail in the real service's response shape so callers' error handling applies.
  public connectToDevice$(_deviceId: string): Observable<BleServiceResponse<BleDeviceInfoExtended>> {
    return of({ success: false, error: 'Mock hunting device does not support manual pairing — activate it from the Test section.' });
  }

  public stopScan(): Promise<void> {
    return Promise.resolve();
  }

  public get isConnected(): boolean {
    return this.connectionStateSubject$.value === BleConnectionState.CONNECTED;
  }

  public get isBleEnabled(): boolean {
    return this.bleEnabledSubject$.value;
  }

  public get currentConnectionState(): BleConnectionState {
    return this.connectionStateSubject$.value;
  }

  public getScannedDevices(): Array<BleDeviceInfoExtended> {
    return this.scannedDevicesSubject$.value;
  }

  private patchDeviceData(patch: Partial<DeviceData>): void {
    this.deviceDataSubject$.next({ ...this.deviceDataSubject$.value, ...patch });
  }

  private ok$<T>(data: T): Observable<BleServiceResponse<T>> {
    return of({ success: true, data });
  }
}
